import type { SseEvent, SseSubscription } from './fetchSse';
import { bffEventStream } from './httpClient';

/** EPS multiplexed notification hub (SPECS §7).
 * BFF-relative path — the adapter's baseUrl already carries the `/v1` prefix
 * (matches every other BFF call site, e.g. `/conversations/{id}/status/stream`). */
export const EPS_HUB_STREAM_PATH = '/events/stream';

export type NotificationListener = (event: SseEvent) => void;

let hub: SseSubscription | null = null;
const listeners = new Set<NotificationListener>();

function teardownHub(): void {
  hub?.close();
  hub = null;
}

function ensureHub(): void {
  if (hub) {
    return;
  }
  const subscription = bffEventStream(EPS_HUB_STREAM_PATH);
  hub = subscription;
  void (async () => {
    try {
      for await (const event of subscription.events()) {
        for (const listener of [...listeners]) {
          listener(event);
        }
      }
    } catch {
      /* subscription closed or aborted */
    }
  })();
}

/**
 * Subscribe to the single shell-owned EPS hub connection.
 * The first subscriber opens the stream; the last closer tears it down.
 */
export function subscribeNotifications(listener: NotificationListener): () => void {
  listeners.add(listener);
  ensureHub();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      teardownHub();
    }
  };
}

/** Test-only: drop hub state between cases. */
export function resetNotificationHubForTests(): void {
  listeners.clear();
  teardownHub();
}
